<template>
  <modal-default ref="modal" title="Comps Data" :width="1600">
    <template v-if="!hasCompFeature">
      <app-upgrade-required class="py-200" is-centered is-large />
    </template>

    <app-loading-section v-else :loading="isLoading">
      <template v-if="showNoCompMessage">
        <p class="property_error--heading">There was an issue pulling comps data for this address.</p>
        <p class="property_error--message">Error: {{ property.real_estate_comp_data_error_message }}</p>
      </template>

      <div v-else-if="compsData" style="min-height: 300px">
        <div class="info-container flex p-50 mb-150">
          <icon-default size="is-small is-text mr-25" icon="info" />
          <p class="is-italic">
            Comp data is sourced from official county records and may differ slightly from Zillow or <a href="https://realtor.com/" target="_blank">Realtor.com</a>.
            This is expected. Other platforms pull from MLS or user-submitted updates, which update faster than county records.
            This is not a data error. For more info, <a href="https://help.forefrontcrm.com/knowledge-base/pipeline/how-to-use-the-comp-tool/" target="_blank">click here</a>.
          </p>
        </div>

        <!-- Current Property -->
        <div class="current_property">
          <icon-default class="current_property--icon" icon="lead" size="is-large"/>

          <p class="current_property--address">
            {{ property.line_1 }}<br>
            <small>{{ property.city }}, {{ property.state }} {{ property.postcode }}</small>
          </p>

          <div class="current_property--info">
            <div class="current_property--info--amounts">
              <p class="property_line">Last Sale Amount: <span>{{ formatCurrency(propertyAmounts.price) }}</span></p>
              <p class="property_line">$/SqFt: <span>{{ formatCurrency(propertyAmounts.pricePerSqFt) }}</span></p>
            </div>

            <div class="current_property--info--details">
              <p class="property_line">Bedrooms: <span>{{ formatValue(property.bedrooms) }}</span></p>
              <p class="property_line">Bathrooms: <span>{{ formatValue(property.bathrooms) }}</span></p>
              <p class="property_line">House Size: <span>{{ formatValue(property.house_size) }}</span></p>
              <p class="property_line">Lot Size: <span>{{ formatValue(property.lot_size) }}</span></p>
              <p class="property_line">Year Built: <span>{{ formatValue(property.year_built) }}</span></p>
            </div>
          </div>
        </div>

        <!-- Averages -->
        <div class="averages">
          <div class="averages--details">
            <div>
              <p class="property_line">Average Amount: <span>{{ formatCurrency(medianValues.amount) }}</span></p>
              <p class="property_line">Price Range:
                <span>
                {{ formatCurrency(medianValues.lowestPrice) }} - {{ formatCurrency(medianValues.highestPrice) }}
              </span>
              </p>
              <p class="property_line">Average $/SqFt: <span>{{ formatCurrency(medianValues.pricePerSqFt) }}</span></p>
            </div>

            <div>
              <p class="property_line">Average Beds: <span>{{ formatValue(medianValues.beds) }}</span></p>
              <p class="property_line">Average Baths: <span>{{ formatValue(medianValues.baths) }}</span></p>
              <p class="property_line">Average Year Built: <span>{{ formatValue(medianValues.yearBuilt) }}</span></p>
            </div>

            <div>
              <p class="property_line">Average Size: <span>{{ formatValue(medianValues.houseSize) }}</span></p>
              <p class="property_line">Average Lot Size: <span>{{ formatValue(medianValues.lotSize) }}</span></p>
              <p class="property_line">Average Distance: <span>{{ formatValue(medianValues.distance) }}</span></p>
            </div>
          </div>
        </div>

        <!-- ARV & MAO Calculations -->
        <div class="arv_calculation">
          <div class="arv_calculation--details">
            <div class="mb-75">
              <form-field-currency
                class="mr-75"
                label="Repair Cost"
                v-model="repairCost"
                @change="saveCompCalculation"
              />
              <form-field-input label="MAO Percentage" v-model="maoPercentage" @change="saveCompCalculation">
                <template v-slot:after>
                  <p class="control">
                    <span class="button is-small field-button">%</span>
                  </p>
                </template>
              </form-field-input>
            </div>

            <div>
              <p class="property_line">
                After Repair Value:
                <span>{{ formatCurrency(calculateArv.arv) }}</span>
              </p>
              <p class="help">ARV = Comps Average $/SqFt * Property House Size</p>

              <p class="property_line">
                Maximum Allowed Offer:
                <span>{{
                  calculateArv.mao
                    ? formatCurrency(calculateArv.mao)
                    : "Enter repair costs to calculate"
                }}</span>
              </p>
              <p class="help">MAO = (ARV * MAO Percentage) - Repair Cost</p>
            </div>
          </div>
        </div>

        <div class="comps">
          <b-loading :active="isUpdating" :is-full-page="false" />

          <div class="mr-100" style="width: 450px">
            <gmap-map ref="map" :center="propertyCoordinates" :zoom="15" style="width: 450px; height: 400px">
              <gmap-info-window
                :options="infoWindow.options"
                :position="infoWindow.position"
                :opened="infoWindow.isOpen"
                @domready="infoWindowDomReady(infoWindow.currentId)"
                @closeclick="infoWindowClose"
              />

              <gmap-marker
                :position="propertyCoordinates"
                :title="propertyAddress"
                icon="https://maps.google.com/mapfiles/ms/micons/green-dot.png"
              />

              <gmap-marker
                v-for="comp in compsData"
                :key="comp.id"
                :position="{
                  lat: formatNumber(comp.latitude),
                  lng: formatNumber(comp.longitude),
                }"
                :clickable="true"
                :title="comp.address.address"
                :icon="compIconMarker(comp.include_in_arv)"
                @click="toggleInfoWindow(comp)"
              />
            </gmap-map>
          </div>

          <!-- Comp Tables -->
          <div style="overflow: hidden">
            <b-table :data="compsData" sticky-header striped narrowed>
              <b-table-column label="Address" v-slot="props" sticky>
                <div class="flex flex-vertical-center" style="width: 240px">
                  <form-field-checkbox
                    v-model="props.row.include_in_arv"
                    @change="saveCompChanges"
                  />

                  <div>
                    {{ props.row.address.street }}<br>
                    <small>{{ props.row.address.city }}, {{ props.row.address.state }} {{
                        props.row.address.zip
                      }}</small>
                  </div>
                </div>
              </b-table-column>

              <b-table-column field="lastSaleDate" label="Date" v-slot="props">
                <div style="width: 120px">
                  {{ formatDate(props.row.lastSaleDate) }}
                </div>
              </b-table-column>

              <b-table-column
                field="estimatedValue"
                label="Amount"
                v-slot="props"
              >
                {{ formatCurrency(props.row.estimatedValue) }}
              </b-table-column>

              <b-table-column field="bedrooms" label="Beds" v-slot="props">
                {{ formatValue(props.row.bedrooms) }}
              </b-table-column>

              <b-table-column field="bathrooms" label="Baths" v-slot="props">
                {{ formatValue(props.row.bathrooms) }}
              </b-table-column>

              <b-table-column field="squareFeet" label="Size" v-slot="props">
                {{ formatValue(props.row.squareFeet) }}
              </b-table-column>

              <b-table-column
                field="lotSquareFeet"
                label="Lot Size"
                v-slot="props"
              >
                {{ formatValue(props.row.lotSquareFeet) }}
              </b-table-column>

              <b-table-column
                field="yearBuilt"
                label="Year Built"
                v-slot="props"
              >
                {{ formatValue(props.row.yearBuilt) }}
              </b-table-column>

              <b-table-column
                field="pricePerSqFt"
                label="$/SqFt"
                v-slot="props"
              >
                {{ formatCurrency(props.row.pricePerSqFt) }}
              </b-table-column>

              <b-table-column
                field="pricePerSqFt"
                label="Distance"
                v-slot="props"
              >
                {{ formatValue(props.row.distance) }}
              </b-table-column>

              <b-table-column
                field="cashBuyer"
                label="Cash Buyer?"
                v-slot="props"
              >
                {{ formatBoolean(props.row.cashBuyer) }}
              </b-table-column>

              <b-table-column
                field="privateLender"
                label="Private Lender?"
                v-slot="props"
              >
                {{ formatBoolean(props.row.privateLender) }}
              </b-table-column>

              <b-table-column
                field="corporateOwned"
                label="Corporate Owned?"
                v-slot="props"
              >
                {{ formatBoolean(props.row.corporateOwned) }}
              </b-table-column>

              <b-table-column
                field="preForeclosure"
                label="Pre Foreclosure?"
                v-slot="props"
              >
                {{ formatBoolean(props.row.preForeclosure) }}
              </b-table-column>

              <b-table-column
                field="propertyType"
                label="Property Type"
                v-slot="props"
              >
                {{ formatValue(props.row.propertyType) }}
              </b-table-column>
            </b-table>
          </div>
        </div>
      </div>
    </app-loading-section>

    <template v-slot:footer>
      <button-outlined
        v-if="hasCompFeature && !isLoading"
        @click="refreshCompData"
        >Refresh Comps
      </button-outlined>
    </template>
  </modal-default>
</template>

<script>
import Vue from 'vue';
import utils from "../../services/utils";
import dates from "../../services/dates";
import PropertyModalSection from "./components/PropertyModalSection.vue";
import PropertyModalItem from "./components/PropertyModalItem.vue";
import api from "../../api";
import * as GmapVue from 'gmap-vue';
import AppUpgradeRequired from "../../components/app/AppUpgradeRequired.vue";

/**
 * https://www.npmjs.com/package/gmap-vue
 * https://diegoazh.github.io/gmap-vue/guide/marker.html
 * https://diegoazh.github.io/gmap-vue/guide/info-window.html
 */
Vue.use(GmapVue, {
  load: {
    key: process.env.MIX_GOOGLE_PLACES_BROWSER_API_KEY
  },
});

export default {
  name: "PropertyCompsModal",
  components: {
    AppUpgradeRequired,
    PropertyModalItem,
    PropertyModalSection
  },
  data() {
    return {
      isLoading: false,
      isUpdating: false,
      infoWindow: {
        currentId: null,
        isOpen: false,
        options: {
          content: null,
          pixelOffset: {
            width: 0,
            height: -34
          },
        },
        position: {
          lat: 0,
          lng: 0
        },
      },
      repairCost: 0,
      maoPercentage: 70,
      debounceTimer: null
    }
  },
  props: {
    property: {
      type: Object,
      required: true,
    },
  },
  computed: {
    hasCompFeature() {
      return this.$store.state.accounts.features.Comp;
    },
    showNoCompMessage() {
      return !!this.property.real_estate_comp_data_errored_at
    },
    propertyAddress() {
      return `Current ${this.property.line_1} ${this.property.city} ${this.property.state} ${this.property.postcode}`
    },
    apiData() {
      if (!!this.property.real_estate_property_data_errored_at) return null
      return this.property.real_estate_property_data
    },
    propertyCoordinates() {
      return {
        lat: (this.apiData) ? this.formatNumber(this.apiData.propertyInfo.latitude) : null,
        lng: (this.apiData) ? this.formatNumber(this.apiData.propertyInfo.longitude) : null
      }
    },
    compsData() {
      if (this.showNoCompMessage) return null

      if (!this.property.real_estate_comp_data) return null

      if (this.property.comp_calculations_data) {
        this.repairCost = this.property.comp_calculations_data.repair_cost || 0;
        this.maoPercentage = this.property.comp_calculations_data.mao_percentage || 70;
      }

      return this.property.real_estate_comp_data.map(comp => {
        const price = parseInt(comp.estimatedValue)
        const sqFt = parseInt(comp.squareFeet)

        comp.pricePerSqFt = sqFt ? price / sqFt : null
        comp.distance = this.getDistance(comp.latitude, comp.longitude)

        // We're getting data like 1.000000000 so these lines remove the extra zeros
        comp.bathrooms = parseFloat(comp.bathrooms);
        comp.bedrooms = parseFloat(comp.bedrooms);

        return comp
      })
    },
    includedCompsData() {
      if(!this.compsData) return []

      return this.compsData.filter(comp => {
        return comp.include_in_arv
      })
    },
    propertyAmounts() {
      if(!this.apiData) {
        return {
          price: 0,
          pricePerSqFt: null
        }
      }

      const price = parseInt(this.apiData.estimatedValue)
      const sqFt = parseInt(this.property.house_size)

      return {
        price: price,
        pricePerSqFt: sqFt ? price / sqFt : null
      }
    },
    medianValues() {
      if(this.includedCompsData.length === 0) {
        return {
          amount: null,
          lowestPrice: null,
          highestPrice: null,
          pricePerSqFt: null,
          beds: '--',
          baths: '--',
          yearBuilt: '--',
          houseSize: '--',
          lotSize: '--',
          distance: '--'
        }
      }

      const compsByAmount = this.includedCompsData.sort(function (a, b) {
        const keyA = parseInt(a.estimatedValue)
        const keyB = parseInt(b.estimatedValue)

        if (keyA < keyB) return -1;
        if (keyA > keyB) return 1;
        return 0;
      });

      const numberOfComps = this.includedCompsData.length

      const totalEstimatedValue = this.includedCompsData.reduce((accumulator, comp) => {
        return parseFloat(accumulator) + parseFloat(comp.estimatedValue)
      }, 0)
      const totalPricePerSqFt = this.includedCompsData.reduce((accumulator, comp) => {
        return parseFloat(accumulator) + Math.round(comp.pricePerSqFt * 100) / 100
      }, 0)
      const totalBeds = this.includedCompsData.reduce((accumulator, comp) => {
        return parseFloat(accumulator) + parseFloat(comp.bedrooms)
      }, 0)
      const totalBaths = this.includedCompsData.reduce((accumulator, comp) => {
        return parseFloat(accumulator) + parseFloat(comp.bathrooms)
      }, 0)
      const totalYearBuilt = this.includedCompsData.reduce((accumulator, comp) => {
        return parseFloat(accumulator) + parseFloat(comp.yearBuilt)
      }, 0)
      const totalHouseSize = this.includedCompsData.reduce((accumulator, comp) => {
        return parseFloat(accumulator) + parseFloat(comp.squareFeet)
      }, 0)
      const totalLotSize = this.includedCompsData.reduce((accumulator, comp) => {
        return parseFloat(accumulator) + parseFloat(comp.lotSquareFeet)
      }, 0)
      const totalDistance = this.includedCompsData.reduce((accumulator, comp) => {
        return parseFloat(accumulator) + parseFloat(comp.distance)
      }, 0)

      return {
        amount: numberOfComps ? Math.round(totalEstimatedValue / numberOfComps) : '--',
        lowestPrice: compsByAmount[0].estimatedValue,
        highestPrice: compsByAmount.slice(-1)[0].estimatedValue,
        pricePerSqFt: numberOfComps ? Math.round((totalPricePerSqFt / numberOfComps) * 100) / 100 : '--',
        beds: numberOfComps ? Math.round(totalBeds / numberOfComps) : '--',
        baths: numberOfComps ? Math.round(totalBaths / numberOfComps) : '--',
        yearBuilt: numberOfComps ? Math.round(totalYearBuilt / numberOfComps) : '--',
        houseSize: numberOfComps ? Math.round(totalHouseSize / numberOfComps) : '--',
        lotSize: numberOfComps ? Math.round(totalLotSize / numberOfComps) : '--',
        distance: numberOfComps ? (totalDistance / numberOfComps).toFixed(2) : '--'
      }
    },
    calculateArv() {
      const subject = this.apiData;
      const subjectSqft = parseInt(subject.propertyInfo.livingSquareFeet);

      if (this.includedCompsData.length === 0) {
        return {
          arv: 0,
          mao: null,
          compsUsed: 0,
          avgPricePerSqft: 0,
        };
      }

      // Simple average (no weighting)
      const avgPricePerSqft = this.includedCompsData.reduce((sum, c) => sum + Math.round(c.pricePerSqFt * 100) / 100, 0) / this.includedCompsData.length;

      // Step 4: ARV
      const arv = Math.round((Math.round(avgPricePerSqft * 100) / 100) * subjectSqft);

      // Step 5: MAO (requires user-provided repair cost)
      const mao = this.repairCost > 0
        ? Math.round((arv * (this.maoPercentage / 100)) - this.repairCost)
        : null;

      return {
        arv: arv,
        mao: mao,
        maoPercent: `${this.maoPercentage}%`,
        repairCosts: this.repairCost,
        avgPricePerSqft: Math.round(avgPricePerSqft * 100) / 100,
        subjectSqft: subjectSqft,
      };
    },
  },
  methods: {
    open() {
      this.$refs.modal.open()

      if(this.hasCompFeature) {
        this.loadRealEstateCompData()
      }
    },
    loadRealEstateCompData(isRefresh = false) {
      this.isLoading = true
      api.properties.getRealEstateCompData(this.property.id, isRefresh).finally(() => {
        this.isLoading = false
      })
    },
    saveCompCalculation() {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = setTimeout(() => {
        const payload = {
          repair_cost: this.repairCost,
          mao_percentage: this.maoPercentage,
        };

        api.properties.updateCompCalculationData(this.property.id, { comp_calculations_data: payload })
        .finally(() => {});
      }, 500);
    },
    refreshCompData() {
      this.loadRealEstateCompData(true)
    },
    saveCompChanges() {
      this.isUpdating = true
      api.properties.updateRealEstateCompData(this.property.id, {comps: this.compsData}).finally(() => {
        this.isUpdating = false
      })
    },
    getDistance(latitude, longitude) {
      const lat1 = parseFloat(this.apiData.propertyInfo.latitude)
      const lon1 = parseFloat(this.apiData.propertyInfo.longitude)
      const lat2 = parseFloat(latitude)
      const lon2 = parseFloat(longitude)

      const radlat1 = Math.PI * lat1 / 180;
      const radlat2 = Math.PI * lat2 / 180;
      const theta = lon1 - lon2;
      const radtheta = Math.PI * theta / 180;
      let dist = Math.sin(radlat1) * Math.sin(radlat2) + Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta);
      if (dist > 1) {
        dist = 1;
      }
      dist = Math.acos(dist);
      dist = dist * 180 / Math.PI;
      dist = dist * 60 * 1.1515;
      return dist.toFixed(2);
    },
    compIconMarker(isIncludeInArv) {
      const marker = (isIncludeInArv) ? 'orange-dot' : 'orange'
      return `https://maps.google.com/mapfiles/ms/micons/${marker}.png`
    },
    toggleInfoWindow(comp) {
      const bedrooms = this.formatValue(comp.bedrooms)
      const baths = this.formatValue(comp.bathrooms)
      const year = this.formatValue(comp.yearBuilt)
      const sqft = this.formatValue(comp.squareFeet)
      const lotSize = this.formatValue(comp.lotSquareFeet)
      const isChecked = (comp.include_in_arv) ? 'checked=""' : ''

      this.infoWindow.position.lat = this.formatNumber(comp.latitude)
      this.infoWindow.position.lng = this.formatNumber(comp.longitude)
      this.infoWindow.options.content = `<strong>${comp.address.address}</strong>`
        + `<p style="margin:3px 0 5px;line-height:1.4;">${bedrooms} bedrooms | ${baths} baths | built in ${year} <br> ${sqft} sq ft | ${lotSize} lot size</p>`
        + `<label><input type="checkbox" value="true" id="info-window-checkbox" ${isChecked}> <span>Inlcude In ARV Calculation</span></label>`

      // handle toggle on the same marker
      if (this.infoWindow.currentId === comp.id) {
        this.infoWindow.isOpen = !this.infoWindow.isOpen
        return
      }

      this.infoWindow.isOpen = true
      this.infoWindow.currentId = comp.id
    },
    infoWindowDomReady(compId) {
      document.getElementById("info-window-checkbox").addEventListener("change", (event) => {
        const compDataIndex = this.compsData.findIndex(comp => comp.id === compId)

        // if index not found then do nothing
        if (compDataIndex < 0) return

        this.compsData[compDataIndex].include_in_arv = event.currentTarget.checked

        this.saveCompChanges()
      });
    },
    infoWindowClose() {
      this.infoWindow.isOpen = false
    },
    formatValue(value) {
      if (!value) return '--'
      return value;
    },
    formatBoolean(value) {
      return value ? 'Yes' : 'No';
    },
    formatCurrency(value) {
      if (!value) return '--'
      return utils.formatCurrency(value)
    },
    formatDate(value) {
      if (!value) return '--'
      return dates.formatAsDate(value)
    },
    formatNumber(value) {
      if (!value) return 0
      return parseFloat(value)
    },
  },
}
</script>

<style lang="scss" scoped>
@import "../../styles/variables/variables";

.info-container {
  border: 1px solid $blue;
  border-radius: 2px;

  p {
    line-height: 1.3;
    font-size: 0.9rem;
  }
}

.property_error {
  &--heading {
    margin: 3rem 0 10px;
    font-size: 1.25rem;
    text-align: center;
  }

  &--message {
    margin-bottom: 4rem;
    text-align: center;
  }
}

.property_line {
  span {
    font-weight: bold;
  }
}

.current_property {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  padding: 0 1rem 1rem;
  border-bottom: 1px solid $border;

  &--icon {
    margin-right: 1rem;
    color: $text-light;
  }

  &--address {
    margin-right: 2rem;
    font-size: 1.5rem;
  }

  &--info {
    &--amounts {
      display: flex;
      gap: 1rem;
      margin-bottom: 8px;
    }

    &--details {
      display: flex;
      justify-content: space-between;
      gap: 1rem;
    }
  }
}

.averages,
.arv_calculation {
  display: flex;

  &--title {
    margin-right: 2em;
    font-weight: bold;
  }

  &--details {
    display: flex;
    justify-content: space-between;
    gap: 2rem;
    max-width: 1000px;
    padding: 0 1rem 0.5rem;
  }

  .property_line {
    margin-bottom: 0.5rem;

    span {
      float: right;
      margin-left: 1rem;
    }
  }
}

.arv_calculation {
  padding-top: 1rem;
  border-top: 1px solid $border;
}

.comps {
  position: relative;
  display: flex;
  margin-bottom: 1rem;
  padding-top: 1rem;
  border-top: 1px solid $border;
}

.help {
  margin: 0 0 0.8rem;
  font-style: italic;
  color: $text-muted;
}
</style>
